import useAsync from 'react-use/lib/useAsync';
import {
  Content,
  Header,
  HeaderLabel,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { Breadcrumbs, Grid, Typography } from '@material-ui/core';
import { drEventTaxonomyReferenceDataProxyApiRef, EventsDetailedMap, PromData } from '../../api';
import { useApi } from '@backstage/core-plugin-api';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { BadResponse } from './BadResponse';
import { topicMapping } from './EventComponent';
import { Accordion, AccordionSummary, AccordionDetails } from './Accordion';
import TableContainer from '@material-ui/core/TableContainer';

const transformData = async (eventMap: EventsDetailedMap, totalMetrics: PromData, failedMetrics: PromData) => {
  const results: any = {};

  Object.keys(eventMap).forEach(k => {
    const eventName = k[0].toLowerCase() + k.replace(/Event$/, '').substring(1);

    const v = eventMap[k];
    const obj: any = {
      owners: v?.data_reliability_metadata?.owning_org || ['unknown'],
      totalCount: 0,
      failureCount: 0,
    };

    Object.keys(topicMapping).forEach((topic: string) => {
      obj[topic] = {
        totalCount: 0,
        failureCount: 0,
      };
    });

    results[eventName] = obj;
  });

  totalMetrics.result.forEach(item => {
    const value = Number.parseInt(item.value[1], 10);
    if (item.metric.event in results) {
      results[item.metric.event].totalCount += value;
      results[item.metric.event][item.metric.topic].totalCount += value;
    }
  });

  failedMetrics.result.forEach(item => {
    const value = Number.parseInt(item.value[1], 10);
    if (item.metric.event in results) {
      results[item.metric.event].failureCount += value;
      results[item.metric.event][item.metric.topic].failureCount += value;
    }
  });

  const transformed: any = {};
  Object.keys(results).forEach(k => {
    const v = results[k];
    v.owners.forEach((owner: string) => {
      if (transformed[owner] === undefined) {
        transformed[owner] = {
          totalCount: 0,
          failureCount: 0,
          events: {},
        };
      }

      transformed[owner].totalCount += v.totalCount;
      transformed[owner].failureCount += v.failureCount;
      transformed[owner].events[k] = v;
    });
  });

  return transformed;
};

export const HealthDashboard = ({ mockData }: any) => {
  const apiClient = useApi(drEventTaxonomyReferenceDataProxyApiRef);
  const { value, loading, error } = useAsync(async () => {
    if (mockData !== undefined) {
      return transformData(mockData.eventMap, mockData.totalMetrics, mockData.failedMetrics);
    }

    const eventMap = await apiClient.get<EventsDetailedMap>('digital-analytics-tracking-validator/v1/event');
    if (eventMap instanceof Response) {
      return eventMap;
    }

    const totalMetrics = await apiClient.get<any>(
      'prometheus/api/v1/query?query=sum by (topic, event) (increase (event_count_total{appName="event-health",environment="prod"}[30d]))',
    );
    if (totalMetrics instanceof Response) {
      return totalMetrics;
    }

    const failedMetrics = await apiClient.get<any>(
      'prometheus/api/v1/query?query=sum by (topic, event) (increase (failure_count_total{appName="event-health",environment="prod"}[30d]))',
    );
    if (failedMetrics instanceof Response) {
      return failedMetrics;
    }

    return transformData(eventMap, totalMetrics, failedMetrics);
  });

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  } else if (value instanceof Response) {
    return <BadResponse response={value} />;
  }

  const columns: TableColumn[] = [
    { title: 'Event', field: 'event', sorting: false },
    { title: '# Tested', field: 'total', align: 'right', render: (dat: any) => dat.total.toLocaleString() },
    { title: '# Passed', field: 'passed', align: 'right', render: (dat: any) => dat.passed.toLocaleString() },
    {
      title: 'Health',
      field: 'pct',
      align: 'right',
      defaultSort: 'asc',
      customSort: (a: any, b: any): number => {
        return a.rawPct - b.rawPct;
      },
    },
  ];

  return (
    <Page themeId="tool">
      <Header title="Clickstream Event Health Dashboard" subtitle="Overview of production clickstream health">
        <HeaderLabel label="Owner" value="Data Reliability" />
        <HeaderLabel label="Slack" value="#data-reliability" />
      </Header>
      <Content>
        <Grid direction="column" spacing={3} container>
          <Grid item>
            <Breadcrumbs>
              <Link to="/dr-event-taxonomy-reference-data">Home</Link>
            </Breadcrumbs>
          </Grid>
          <Grid item>
            <Typography variant="h5">Events Health - last 30 days</Typography>
            {Object.keys(value).map(ownerCategory => {
              const health = value[ownerCategory];
              let rawPct = health.totalCount > 0 ? Math.floor(100 * (1 - health.failureCount / health.totalCount)) : -1;
              let pct = rawPct >= 0 ? `${rawPct}%` : 'N/A';
              const title = ownerCategory[0].toUpperCase() + ownerCategory.substring(1);
              return (
                <Accordion square key={`health-dashboard-accordion-${ownerCategory}`}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls={`panel${ownerCategory}-content`}
                    id={`panel${ownerCategory}-header`}
                  >
                    <span style={{ fontSize: '20px' }}>
                      {title} ({Math.max(0, health.totalCount - health.failureCount).toLocaleString()} /{' '}
                      {health.totalCount.toLocaleString()} = {pct})
                    </span>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table
                        options={{ search: false, paging: false, sorting: true, toolbar: false }}
                        style={{ width: '100%' }}
                        columns={columns}
                        data={Object.keys(health.events).map((event): any => {
                          const val = health.events[event];
                          rawPct = val.totalCount > 0 ? Math.floor(100 * (1 - val.failureCount / val.totalCount)) : -1;
                          pct = rawPct >= 0 ? `${rawPct}%` : 'N/A';
                          return {
                            event: (
                              <Link to={`/dr-event-taxonomy-reference-data/events/${event}`} underline="always">
                                {event}
                              </Link>
                            ),
                            total: val.totalCount,
                            passed: Math.max(0, val.totalCount - val.failureCount),
                            pct,
                            rawPct,
                          };
                        })}
                      />
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
